import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, User, Loader2, CheckCircle2, ShieldAlert } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Motion";
import { usePageMeta } from "@/components/Common";
import { API, AREAS, PHONE, TEL, EMAIL, AREAS_TEXT, SQFT_OPTIONS, VISIT_OPTIONS, SERVICE_TYPE_OPTIONS, CONCIERGE_OPTIONS, ONE_TIME_FORM_NOTE } from "@/data/site";

const EMPTY = {
  full_name: "", phone: "", email: "", city: "", property_address: "", square_footage: "", property_type: "", absence_dates: "",
  visits_per_month: "", service_type: "", concierge_services: [], contact_method: "Phone", comments: "", consent: false, website: "",
};

const field = "w-full rounded-[3px] border border-[#14263D]/25 bg-white px-4 py-3 text-base text-[#14263D] placeholder:text-[#5F6B76]/70 transition-colors focus:border-[#A52A32] focus:outline-none focus:ring-2 focus:ring-[#A52A32]/20";
const label = "mb-2 block font-mono text-[0.7rem] uppercase tracking-[0.16em] text-[#14263D]";

const Field = ({ id, text, required, children }) => (
  <div>
    <label htmlFor={id} className={label}>{text}{required && <span className="text-[#A52A32]"> *</span>}</label>
    {children}
  </div>
);

const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => `${x?.loc?.slice(-1)[0] ?? ""}: ${x?.msg ?? ""}`).join(" · ");
  return "We couldn't send your request right now.";
};

const ConsultForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const toggleService = (s) => setForm((f) => ({
    ...f,
    concierge_services: f.concierge_services.includes(s) ? f.concierge_services.filter((x) => x !== s) : [...f.concierge_services, s],
  }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError("");
    try {
      await axios.post(`${API}/consultations`, form);
      setStatus("success");
      setForm(EMPTY);
    } catch (err) {
      setError(errMsg(err));
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <motion.div data-testid="contact-success-message" role="status" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} className="rounded-[4px] bg-[#14263D] p-10 text-white md:p-14">
        <CheckCircle2 className="h-12 w-12 text-[#f0b3b7]" />
        <p className="mt-6 font-display text-3xl leading-snug md:text-4xl">Thank you for contacting First Due Home Watch &amp; Concierge.</p>
        <p className="mt-4 text-base leading-relaxed text-white/85">Your request has been received. We look forward to speaking with you.</p>
        <button type="button" data-testid="contact-send-another-btn" onClick={() => setStatus("idle")} className="mt-8 text-sm font-semibold underline underline-offset-4">Send another request</button>
      </motion.div>
    );
  }

  return (
    <form data-testid="consultation-form" onSubmit={submit} className="rounded-[4px] border border-[#14263D]/10 bg-white p-6 shadow-[0_30px_80px_-50px_rgba(20,38,61,0.5)] md:p-10">
      <h2 className="font-display text-3xl font-semibold text-[#14263D] md:text-4xl">Request a Personalized Quote</h2>
      <p className="mt-3 flex items-start gap-2 text-sm text-[#5F6B76]">
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-[#A52A32]" />
        Please do not include alarm codes, passwords, keys or other access information. We'll discuss those privately.
      </p>
      <div className="mt-8 grid gap-5 md:grid-cols-2">
        <Field id="full_name" text="Customer name" required><input id="full_name" data-testid="contact-name-input" required minLength={2} value={form.full_name} onChange={set("full_name")} className={field} autoComplete="name" /></Field>
        <Field id="phone" text="Phone number" required><input id="phone" type="tel" data-testid="contact-phone-input" required minLength={7} value={form.phone} onChange={set("phone")} className={field} autoComplete="tel" /></Field>
        <Field id="email" text="Email address" required><input id="email" type="email" data-testid="contact-email-input" required value={form.email} onChange={set("email")} className={field} autoComplete="email" /></Field>
        <Field id="city" text="Property city or community">
          <select id="city" data-testid="contact-city-select" value={form.city} onChange={set("city")} className={field}>
            <option value="">Select a community</option>
            {AREAS.map((a) => <option key={a}>{a}</option>)}
            <option>Other / Not sure</option>
          </select>
        </Field>
        <Field id="address" text="Property address (optional)"><input id="address" data-testid="contact-address-input" maxLength={200} value={form.property_address} onChange={set("property_address")} className={field} autoComplete="street-address" /></Field>
        <Field id="sqft" text="Approximate home square footage">
          <select id="sqft" data-testid="contact-sqft-select" value={form.square_footage} onChange={set("square_footage")} className={field}>
            <option value="">Select</option>
            {SQFT_OPTIONS.map((o) => <option key={o}>{o}</option>)}
          </select>
        </Field>
        <Field id="ptype" text="Property type">
          <select id="ptype" data-testid="contact-property-type-select" value={form.property_type} onChange={set("property_type")} className={field}>
            <option value="">Select</option>
            {["Single-family home", "Townhome", "Condominium", "Estate / acreage property", "Other"].map((o) => <option key={o}>{o}</option>)}
          </select>
        </Field>
        <Field id="dates" text="Expected travel or absence dates"><input id="dates" data-testid="contact-dates-input" placeholder="e.g. May 15 – October 30" value={form.absence_dates} onChange={set("absence_dates")} className={field} /></Field>
        <Field id="visits" text="Desired visit frequency">
          <select id="visits" data-testid="contact-visits-select" value={form.visits_per_month} onChange={set("visits_per_month")} className={field}>
            <option value="">Select</option>
            {VISIT_OPTIONS.map((o) => <option key={o}>{o}</option>)}
          </select>
          {form.visits_per_month === "One-Time" && (
            <p data-testid="contact-one-time-note" className="mt-2 text-sm leading-relaxed text-[#5F6B76]">{ONE_TIME_FORM_NOTE}</p>
          )}
        </Field>
        <Field id="stype" text="Type of service needed">
          <select id="stype" data-testid="contact-service-type-select" value={form.service_type} onChange={set("service_type")} className={field}>
            <option value="">Select</option>
            {SERVICE_TYPE_OPTIONS.map((o) => <option key={o}>{o}</option>)}
          </select>
        </Field>
        <fieldset className="md:col-span-2">
          <legend className={label}>Additional concierge services requested</legend>
          <div className="flex flex-wrap gap-2">
            {CONCIERGE_OPTIONS.map((s) => {
              const on = form.concierge_services.includes(s);
              return (
                <label key={s} className={`cursor-pointer rounded-full border px-4 py-2 text-sm font-medium transition-colors ${on ? "border-[#14263D] bg-[#14263D] text-white" : "border-[#14263D]/25 text-[#14263D] hover:border-[#14263D]"}`}>
                  <input type="checkbox" checked={on} onChange={() => toggleService(s)} data-testid={`contact-service-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="sr-only" />
                  {s}
                </label>
              );
            })}
          </div>
        </fieldset>
        <fieldset className="md:col-span-2">
          <legend className={label}>Preferred contact method</legend>
          <div className="flex flex-wrap gap-3">
            {["Phone", "Text", "Email"].map((m) => (
              <label key={m} className={`cursor-pointer rounded-full border px-5 py-2.5 text-sm font-medium transition-colors ${form.contact_method === m ? "border-[#14263D] bg-[#14263D] text-white" : "border-[#14263D]/25 text-[#14263D] hover:border-[#14263D]"}`}>
                <input type="radio" name="contact_method" value={m} checked={form.contact_method === m} onChange={set("contact_method")} data-testid={`contact-method-${m.toLowerCase()}`} className="sr-only" />
                {m}
              </label>
            ))}
          </div>
        </fieldset>
        <div className="md:col-span-2">
          <Field id="comments" text="Additional questions or comments"><textarea id="comments" data-testid="contact-comments-input" rows={4} maxLength={2000} value={form.comments} onChange={set("comments")} className={field} /></Field>
        </div>
        <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} className="hidden" aria-hidden="true" />
        <label className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-[#14263D] md:col-span-2">
          <input type="checkbox" required checked={form.consent} onChange={set("consent")} data-testid="contact-consent-checkbox" className="mt-1 h-4 w-4 accent-[#A52A32]" />
          I agree to be contacted by First Due Home Watch &amp; Concierge about this inquiry. <span className="text-[#A52A32]">*</span>
        </label>
      </div>
      {status === "error" && (
        <p data-testid="contact-error-message" role="alert" className="mt-6 rounded-[3px] border border-[#A52A32]/40 bg-[#A52A32]/5 p-4 text-sm text-[#8B2329]">
          {error} Please call us at <a href={TEL} className="font-semibold underline">{PHONE}</a>.
        </p>
      )}
      <button type="submit" disabled={status === "sending"} data-testid="contact-submit-btn" className="btn-sweep mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#A52A32] px-8 py-4 text-base font-semibold text-white disabled:opacity-70 md:w-auto">
        {status === "sending" && <Loader2 className="h-4 w-4 animate-spin" />}
        Request My Personalized Quote
      </button>
    </form>
  );
};

const Info = () => (
  <aside className="space-y-8">
    <a href={TEL} data-testid="contact-phone-card" className="group block rounded-[4px] bg-[#A52A32] p-8 text-white transition-transform duration-500 hover:-translate-y-1">
      <p className="font-mono text-xs uppercase tracking-[0.2em] text-white/85">The fastest way to reach us</p>
      <p className="mt-4 flex items-center gap-3 font-display text-4xl font-semibold"><Phone className="h-7 w-7 transition-transform group-hover:-rotate-12" />{PHONE}</p>
      <p className="mt-2 text-sm text-white/90">Tap to call Dwight directly.</p>
    </a>
    <div className="rounded-[4px] border border-[#14263D]/15 bg-white p-8">
      <p className="font-display text-2xl font-semibold text-[#14263D]">First Due Home Watch &amp; Concierge</p>
      <ul className="mt-5 space-y-4 text-sm text-[#14263D]">
        <li className="flex gap-3"><User className="h-4 w-4 text-[#A52A32]" />Owner: Dwight Saxon</li>
        <li className="flex gap-3"><Phone className="h-4 w-4 text-[#A52A32]" /><a href={TEL} className="underline-offset-4 hover:underline">Phone: {PHONE}</a></li>
        <li className="flex gap-3"><Mail className="h-4 w-4 text-[#A52A32]" /><a href={`mailto:${EMAIL}`} data-testid="contact-email-link" className="break-all underline-offset-4 hover:underline">Email: {EMAIL}</a></li>
        <li data-testid="contact-location" className="flex gap-3"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#A52A32]" />Based in Wellington, FL 33414 | Serving Western Palm Beach County</li>
      </ul>
      <p className="mt-6 border-t border-[#14263D]/10 pt-5 text-sm leading-relaxed text-[#5F6B76]">Service Areas: {AREAS_TEXT}.</p>
      <p className="mt-3 text-xs text-[#5F6B76]">Not a walk-in office. Consultations by appointment.</p>
    </div>
  </aside>
);

export default function Contact() {
  usePageMeta("Request a Personalized Home Watch Quote | First Due Home Watch & Concierge | 561-722-3224", "Request a personalized home watch quote in Wellington, Royal Palm Beach, Westlake, Arden or Loxahatchee. Free consultation. Call Dwight at 561-722-3224.");
  return (
    <main data-testid="contact-page">
      <PageHero
        testId="contact-hero"
        eyebrow="Let's Talk About Your Home."
        lines={["A Dependable Local", "Contact Starts With", "a Conversation."]}
        intro="Whether you're heading north for the season, planning an extended vacation or leaving your home temporarily unoccupied, First Due Home Watch & Concierge is ready to discuss your needs."
        img="/images/interior2.jpg"
        alt="Bright home interior"
      />
      <section className="bg-[#F8F7F3] py-20 md:py-28">
        <div className="mx-auto grid max-w-[1400px] gap-10 px-5 md:px-10 lg:grid-cols-12">
          <Reveal className="lg:col-span-4 lg:order-2"><Info /></Reveal>
          <Reveal className="lg:col-span-8 lg:order-1"><ConsultForm /></Reveal>
        </div>
      </section>
    </main>
  );
}
